import {
  Close,
  Email,
  Phone,
  LinkedIn,
  ChatBubbleOutline,
  ListAlt,
  Timeline as TimelineIcon,
  OpenInNew,
  LocationOn,
} from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Card,
  CardContent,
  Link,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import moment from "moment";
import { hostname, parseError } from "../../../utils";
import {
  SEQUENCE_MEMBER_STATUS_ACTIVE,
  SEQUENCE_MEMBER_STATUS_UNSUBSCRIBED,
  SEQUENCE_MEMBER_STATUS_BOUNCED,
  SEQUENCE_MEMBER_STATUS_PAUSED,
  SEQUENCE_MEMBER_STATUS_REMOVED,
} from "../../../constants";

interface SequenceMemberProfileDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  sequenceId: string;
  memberId: string;
  getToken: () => Promise<string>;
}

interface ProfileData {
  member: any;
  smartLists: Array<{
    listId: string;
    listName: string;
    listType: string;
    rows: Array<{
      rowId: string;
      createdDate: string;
      columns: Array<{ header: string; value: string }>;
    }>;
  }>;
  agentConversations: Array<{
    conversationId: string;
    agentId: string;
    agentName: string;
    channel: string;
    contactInfo: any;
    status: string;
    startedAt: number;
    lastActivityAt: number;
    messageCount: number;
    createdDate: string;
  }>;
  otherSequences: Array<{
    sequenceId: string;
    sequenceName: string;
    memberId: string;
    status: string;
    currentStep: any;
    lastStepDate: string;
    createdDate: string;
  }>;
}

const SequenceMemberProfileDialog: React.FC<
  SequenceMemberProfileDialogProps
> = ({ open, onClose, companyId, sequenceId, memberId, getToken }) => {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [error, setError] = useState<string | null>(null);


  const loadProfile = async () => {
    if (!memberId || !sequenceId) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/sequences/${sequenceId}/members/${memberId}/profile`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProfile(response.data);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && memberId && sequenceId) {
      loadProfile();
    }
  }, [open, memberId, sequenceId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case SEQUENCE_MEMBER_STATUS_ACTIVE:
        return "primary";
      case SEQUENCE_MEMBER_STATUS_PAUSED:
        return "warning";
      case SEQUENCE_MEMBER_STATUS_UNSUBSCRIBED:
      case SEQUENCE_MEMBER_STATUS_BOUNCED:
      case SEQUENCE_MEMBER_STATUS_REMOVED:
        return "error";
      default:
        return "default";
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const renderMemberInfo = () => {
    if (!profile?.member) return null;

    const { member } = profile;

    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {member.firstName} {member.lastName}
              </Typography>
              <Chip
                label={
                  (member.status || SEQUENCE_MEMBER_STATUS_ACTIVE)
                    .charAt(0)
                    .toUpperCase() +
                  (member.status || SEQUENCE_MEMBER_STATUS_ACTIVE).slice(1)
                }
                color={getStatusColor(member.status || SEQUENCE_MEMBER_STATUS_ACTIVE) as any}
                size="small"
                sx={{ mb: 2 }}
              />
            </Grid>

            {member.email && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">{member.email}</Typography>
                </Box>
              </Grid>
            )}

            {member.phone && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Phone fontSize="small" color="action" />
                  <Typography variant="body2">{member.phone}</Typography>
                </Box>
              </Grid>
            )}

            {member.linkedinUrl && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinkedIn fontSize="small" color="action" />
                  <Link
                    href={/^https?:\/\/[a-zA-Z0-9]/.test(member.linkedinUrl) ? member.linkedinUrl : "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    LinkedIn Profile
                  </Link>
                </Box>
              </Grid>
            )}

            {member.address && Object.values(member.address).some((v) => v) && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2">
                      {member.address.address}
                      {member.address.address2 && `, ${member.address.address2}`}
                    </Typography>
                    <Typography variant="body2">
                      {[
                        member.address.city,
                        member.address.state,
                        member.address.postalCode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      {member.address.country &&
                        member.address.country !== "US" &&
                        ` ${member.address.country}`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {member.created_date && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Added: {moment(member.created_date).format("MMM D, YYYY h:mm A")}
                </Typography>
              </Grid>
            )}

            {member.userData && Object.keys(member.userData).length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Custom Data
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {Object.entries(member.userData).map(([key, value]) => (
                    <Typography key={key} variant="body2" color="text.secondary">
                      <strong>{key}:</strong> {String(value)}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderSmartLists = () => {
    if (!profile?.smartLists || profile.smartLists.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            This member doesn't appear in any smart lists.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {profile.smartLists.map((list) => (
          <Paper key={list.listId} variant="outlined" sx={{ mb: 2, p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ListAlt color="primary" />
              <Link
                href={`/dashboard/smartlists?listId=${list.listId}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="h6"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {list.listName}
                <OpenInNew fontSize="small" />
              </Link>
              <Chip label={list.listType} size="small" sx={{ ml: 1 }} />
            </Box>

            {list.rows.map((row) => (
              <Box key={row.rowId} sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Row added: {moment(row.createdDate).format("MMM D, YYYY")}
                </Typography>
                <Box sx={{ mt: 1, pl: 2 }}>
                  {row.columns.map((col, idx) => (
                    <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                      <strong>{col.header}:</strong> {col.value}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        ))}
      </Box>
    );
  };

  const renderAgentConversations = () => {
    if (!profile?.agentConversations || profile.agentConversations.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No agent conversations found for this member.
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {profile.agentConversations.map((conv, index) => (
          <Box key={conv.conversationId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ChatBubbleOutline fontSize="small" color="primary" />
                    <Typography variant="subtitle1">{conv.agentName}</Typography>
                    <Chip
                      label={conv.status}
                      size="small"
                      color={conv.status === "active" ? "success" : "default"}
                    />
                    <Chip label={conv.channel} size="small" variant="outlined" />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {conv.messageCount} message{conv.messageCount !== 1 ? "s" : ""}
                    </Typography>
                    {conv.contactInfo && (
                      <Typography variant="body2" color="text.secondary">
                        Contact: {JSON.stringify(conv.contactInfo)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      Started: {moment.unix(conv.startedAt / 1000).format("MMM D, YYYY h:mm A")}
                    </Typography>
                    {conv.lastActivityAt && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last activity:{" "}
                        {moment.unix(conv.lastActivityAt / 1000).format("MMM D, YYYY h:mm A")}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
            {index < profile.agentConversations.length - 1 && <Divider />}
          </Box>
        ))}
      </List>
    );
  };

  const renderOtherSequences = () => {
    if (!profile?.otherSequences || profile.otherSequences.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            This member is not in any other sequences.
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {profile.otherSequences.map((seq, index) => (
          <Box key={seq.memberId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TimelineIcon fontSize="small" color="primary" />
                    <Link
                      href={`/dashboard/sequences?sequenceId=${seq.sequenceId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="subtitle1"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {seq.sequenceName}
                      <OpenInNew fontSize="small" />
                    </Link>
                    <Chip
                      label={
                        seq.status.charAt(0).toUpperCase() + seq.status.slice(1)
                      }
                      size="small"
                      color={getStatusColor(seq.status) as any}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    {seq.lastStepDate && (
                      <Typography variant="body2" color="text.secondary">
                        Last step: {moment(seq.lastStepDate).format("MMM D, YYYY h:mm A")}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      Added: {moment(seq.createdDate).format("MMM D, YYYY")}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            {index < profile.otherSequences.length - 1 && <Divider />}
          </Box>
        ))}
      </List>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Member Profile</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box p={3}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && profile && (
          <Box>
            {renderMemberInfo()}

            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              <Tab
                label={`Smart Lists (${profile.smartLists?.length || 0})`}
                icon={<ListAlt />}
                iconPosition="start"
              />
              <Tab
                label={`Agent Conversations (${profile.agentConversations?.length || 0})`}
                icon={<ChatBubbleOutline />}
                iconPosition="start"
              />
              <Tab
                label={`Other Sequences (${profile.otherSequences?.length || 0})`}
                icon={<TimelineIcon />}
                iconPosition="start"
              />
            </Tabs>

            {currentTab === 0 && renderSmartLists()}
            {currentTab === 1 && renderAgentConversations()}
            {currentTab === 2 && renderOtherSequences()}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SequenceMemberProfileDialog;
